import { Sort, SortFn, SortOptions } from "@fgb/core";
import { MonoTypeOperatorFunction, map, pipe } from "rxjs";


/**
 * Sort an array of objects by a property or a function
 * @example 
 *  const items = [{ name: 'in', lastname: 'wakwak' }, { name: 'sik', lastname: 'kuyamad }];
 *  items.sort(sortBy('name'));
 * 
 *  you can also pass a function to sort the array
 *  @example
 *  items.sort(sortBy((a, b) => a.name.localeCompare(b.name)));
 * 
 *  you can also pass a second parameter to specify the order of the sort
 *  @example
 *  sort by name in descending order
 *  items.sort(sortBy('name', 'desc'));
 * 
 *  @example
 *  for sorting by date, add the type option because sometimes it fails when validating the string using luxon
 *  items.sort(sortBy('date', 'asc', { type: 'date' }));
 *  
 *  OR
 *  
 *  use
 *  @see {@link sortDate}
 * 
 *  for precision use the options parameter to specify the type of the property you want to sort
 */
export function sortBy(prop: string | SortFn, order: 'asc' | 'desc' = 'asc', options?: SortOptions): SortFn{
    return Sort.by(prop, order, options);
}

export function sortMultiple(...args: SortFn[]){
    return Sort.multiple(...args);
}

/**
 * Luxon date validation sometimes fails esp. when using DateTime.fromISO('10').isValid -> true
 * 
 * @example
 * items.sort(sortDate('StartDate', 'asc'));
 */
export function sortDate(prop: string, order: 'asc' | 'desc' = 'asc'): SortFn{
    return Sort.byDate(prop, order);
}

/**
 * Pipe operator for observable for sorting an array of objects by passing parameter function or property name. default order is ascending
 * @example
 *  this.items$ = this.lottosQuery.selectAll()
 *   .pipe(
 *      sortPipe('name')
 *   );
 * 
 * you can also pass a second parameter to specify the order of the sort
 * @example
 * this.items$ = this.lottosQuery.selectAll()
 *  .pipe(
 *    sortPipe(
 *     sortBy('name', 'desc'),
 *     sortBy('date', 'asc', { type: 'date' })
 *    )
 *  )
 */
export function sortPipe(...args: SortFn[]): MonoTypeOperatorFunction<any>{
    return pipe(map((items) => items.sort(sortMultiple(...args))));
}